import axios from 'axios';
import { withCache, dataCache } from '../utils/cache';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  registerUser: (data) => {
    dataCache.invalidatePattern('^users_');
    return api.post('/auth/register-user', data);
  },
  getCompanyUsers: (role) => {
    const cacheKey = `users_${role || 'all'}`;
    return withCache(cacheKey, () => api.get(`/auth/users${role ? `?role=${role}` : ''}`), 3 * 60 * 1000);
  },
  updateUser: (userId, data) => {
    dataCache.invalidatePattern('^users_');
    return api.put(`/auth/users/${userId}`, data);
  },
  deleteUser: (userId) => {
    dataCache.invalidatePattern('^users_');
    return api.delete(`/auth/users/${userId}`);
  },
  updateCompany: (data) => api.put('/auth/company', data),
};

export const truckAPI = {
  getAll: (params) => {
    const cacheKey = `trucks_${JSON.stringify(params || {})}`;
    return withCache(cacheKey, () => api.get('/trucks', { params }), 2 * 60 * 1000);
  },
  getById: (id) => api.get(`/trucks/${id}`),
  create: (data) => {
    dataCache.invalidatePattern('^trucks_');
    return api.post('/trucks', data);
  },
  update: (id, data) => {
    dataCache.invalidatePattern('^trucks_');
    return api.put(`/trucks/${id}`, data);
  },
  delete: (id) => {
    dataCache.invalidatePattern('^trucks_');
    return api.delete(`/trucks/${id}`);
  },
};

export const maintenanceAPI = {
  getAll: (params) => api.get('/maintenance', { params }),
  getByTruck: (truckId) => api.get(`/maintenance/truck/${truckId}`),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.put(`/maintenance/${id}`, data),
  delete: (id) => api.delete(`/maintenance/${id}`),
};

export const tripAPI = {
  getAll: (params = {}) => api.get('/trips', { params }),
  getByDriver: (driverId, params = {}) => api.get(`/trips/driver/${driverId}`, { params }),
  create: (data) => api.post('/trips', data),
  update: (id, data) => api.put(`/trips/${id}`, data),
  delete: (id) => api.delete(`/trips/${id}`),
};

export const fuelAPI = {
  getAll: (params) => api.get('/fuel', { params }),
  getByTruck: (truckId) => api.get(`/fuel/truck/${truckId}`),
  create: (data) => api.post('/fuel', data),
  update: (id, data) => api.put(`/fuel/${id}`, data),
  delete: (id) => api.delete(`/fuel/${id}`),
};

export const expenseAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

export const qrCodeAPI = {
  getAll: () => api.get('/qr-codes'),
  getByTruck: (truckId) => api.get(`/qr-codes/truck/${truckId}`),
  create: (data) => api.post('/qr-codes', data),
  update: (id, data) => api.put(`/qr-codes/${id}`, data),
  delete: (id) => api.delete(`/qr-codes/${id}`),
};

export const userAPI = {
  getAlertSettings: () => api.get('/users/me/alert-settings'),
  updateAlertSettings: (data) => api.put('/users/me/alert-settings', data),
};

export const configAPI = {
  getConfiguration: () => withCache('configuration', () => api.get('/configuration'), 10 * 60 * 1000),
  updateServiceTypes: (serviceTypes) => {
    dataCache.invalidate('configuration');
    return api.put('/configuration/service-types', { serviceTypes });
  },
  updateTripLocations: (tripLocations) => {
    dataCache.invalidate('configuration');
    return api.put('/configuration/trip-locations', { tripLocations });
  },
};

export const uploadAPI = {
  uploadImage: (file, recordType = 'general', recordId = null) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('recordType', recordType);
    formData.append('recordId', recordId || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const breakdownAPI = {
  getAll: () => api.get('/breakdown'),
  getActive: () => api.get('/breakdown/active'),
  getById: (id) => api.get(`/breakdown/${id}`),
  create: (data) => api.post('/breakdown', data),
  update: (id, data) => api.put(`/breakdown/${id}`, data),
  delete: (id) => api.delete(`/breakdown/${id}`),
  restore: (id, data) => api.post(`/breakdown/${id}/restore`, data),
};

export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  getCurrent: () => api.get('/subscription/current'),
  subscribe: (data) => api.post('/subscription/subscribe', data),
  createTrial: () => api.post('/subscription/create-trial'),
  getUsage: () => api.get('/subscription/usage'),
  upgrade: (data) => api.put('/subscription/upgrade', data),
  cancel: (data) => api.post('/subscription/cancel', data),
};

export const paymentAPI = {
  createOrder: (data) => api.post('/payment/create-order', data),
  verifyPayment: (data) => api.post('/payment/verify', data),
  processSubscription: (data) => api.post('/payment/process-subscription', data),
  getMethods: () => api.get('/payment/methods'),
  addMethod: (data) => api.post('/payment/methods', data),
  setDefaultMethod: (id) => api.post(`/payment/methods/${id}/default`),
  deleteMethod: (id) => api.delete(`/payment/methods/${id}`),
  getInvoices: () => api.get('/payment/invoices'),
  getInvoiceById: (id) => api.get(`/payment/invoices/${id}`),
  getUpcomingInvoice: () => api.get('/payment/invoices/upcoming'),
};

export default api;
